import { useEffect, useState, memo } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { text as textColor } from 'theme/palette'
import { opacity } from 'theme/globals'

const StyledIcon = styled.svg.withConfig({
  shouldForwardProp: prop => ![
    'icon',
    'color',
    'width',
    'height',
    'growY',
    'growX',
    'maxWidth',
    'maxHeight',
    'neutral',
    'inheritTransition',
    'selectable',
    'blend',
    'hover',
    'hoverBlend',
  ].includes(prop),
})`
  ${props => ({
    color: props.theme.palette[props.color] || props.theme.palette.text[props.color],
    ...(props.width && { width: `${props.width}px` }),
    ...(props.height && { height: `${props.height}px` }),
    ...(props.growY && { height: '100%' }),
    ...(props.growX && { width: '100%' }),
    ...(props.maxWidth && { maxWidth: props.maxWidth }),
    ...(props.maxHeight && { maxHeight: props.maxHeight }),
    ...(props.neutral && { filter: 'grayscale(1)' }),
    ...(props.inheritTransition && { transition: props.theme.internalMixins.setTransition(['stroke', 'fill', 'color', 'opacity']) }),
    ...(props.selectable && { cursor: 'pointer', userSelect: 'none' }),
    ...(props.blend && { opacity: ['string', 'number'].includes(typeof props.blend) ? props.theme.globals.opacity[props.blend] : props.theme.globals.opacity.main }),
    ...(props.hover && props.color && { '&:hover': { color: props.theme.palette.text[`${props.color}Hover`] } }),
    ...(props.hoverBlend && { '&:hover': { opacity: props.theme.globals.opacity[props.hoverBlend] } }),
  })}
`

const Icon = props => {
  const [iconData, setIconData] = useState(null)

  useEffect(() => {
    let isMounted = true
    const fetchData = async () => {
      const { ReactComponent } = await import(`../../icons/${props.icon}.svg`)
      if (isMounted) setIconData(ReactComponent)
    }
    fetchData()
    return () => (isMounted = false)
  }, [props.icon])

  if (!iconData) return null

  return (
    <StyledIcon 
      {...iconData.props} 
      {...props} 
      {...(iconData.props.width && iconData.props.height ? { viewBox: `0 0 ${iconData.props.width} ${iconData.props.height}` } : {})}
    />
  )
}

Icon.propTypes = {
  icon: PropTypes.string,
  color: PropTypes.oneOf(Object.keys(textColor)),
  hover: PropTypes.bool,
  selectable: PropTypes.bool,
  blend: PropTypes.oneOf([true, false, ...Object.keys(opacity)]),
  hoverBlend: PropTypes.oneOf([true, false, ...Object.keys(opacity)]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  growX: PropTypes.bool,
  growY: PropTypes.bool,
  maxWidth: PropTypes.string,
  maxHeight: PropTypes.string,
  neutral: PropTypes.bool,
  inheritTransition: PropTypes.bool,
}

Icon.defaultProps = {
  icon: 'Add',
  color: 'inherit',
}

export default memo(Icon)
